import { replaceWithNonbreakableSpaces } from '~/utils';

export const msToAmPmHours = (ms) => {
  const date = new Date(ms);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();

  const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
  const minutesMoreThenZero = minutes > 0 && minutes < 10 ? `:0${minutes}` : `:${minutes}`;
  const formattedMinutes = minutes > 0 ? minutesMoreThenZero : '';
  const period = hours > 11 ? 'pm' : 'am';

  return `${formattedHours}${formattedMinutes}${period}`;
};

export const msToPartDayTime = (ms) => {
  const date = new Date(ms);
  const hours = date.getUTCHours();

  if (hours < 12) {
    return 'Morning: 7:00am-12:00pm';
  }
  if (hours < 17) {
    return 'Afternoon: 12:00pm-5:00pm';
  }
  return 'Evening: 5:00pm-6:30pm';
};

const msInHour = 1000 * 60 * 60;
const msInMinute = 1000 * 60;

export const msToHours = (ms) => ms / msInHour;

export const msToMinutes = (ms) => ms / msInMinute;

export const formatInHoursAndMinutes = (appointmentTime) => {
  const minutes = appointmentTime % 60;
  const hours = (appointmentTime - minutes) / 60;

  let hoursString = hours > 0 ? `${hours} hour` : '';
  hoursString = hours > 1 ? `${hoursString}s` : hoursString;

  let minutesString = minutes > 0 ? `${minutes} minutes` : '';
  minutesString = hours > 0 && minutes > 0 ? ` and ${minutesString}` : minutesString;

  return `${hoursString}${minutesString}`;
};

const DAYS_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DAYS_FULL_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const MONTHS_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_ORDINALS = ['th', 'st', 'nd', 'rd'];

const addZero = (number) => `${number < 10 ? 0 : ''}${number}`;

const getDayFullName = (day) => DAYS_FULL_NAMES[day] || '';
const getMonthFullName = (month) => MONTHS_NAMES[month] || '';

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

export const createDate = (year, month, date) => {
  const daysInMonth = getDaysInMonth(year, month);
  const day = date > daysInMonth ? daysInMonth : date;
  return new Date(year, month, day);
};

export const createDateWithOffset = (date, month) => {
  const nextMonth = date.getMonth() + month - 1;
  return createDate(date.getFullYear(), nextMonth, date.getDate());
};

export const getDestructedDate = (date, month = 1) => {
  const offsetDate = createDateWithOffset(date, month);
  return {
    year: offsetDate.getFullYear(),
    month: addZero(offsetDate.getMonth() + 1),
    day: addZero(month === 1 ? date.getDate() : 1),
  };
};

const getFormattedDateFromDestructedDate = (year, month, day) => `${year}-${month}-${day}`;

const getFormattedDate = (date) => {
  const { year, month, day } = getDestructedDate(date);
  return getFormattedDateFromDestructedDate(year, month, day);
};

export const getDateFromString = (dateString) => {
  if (dateString) {
    const dateArray = dateString.split('-').map(Number);
    return new Date(dateArray[0], dateArray[1] - 1, dateArray[2], 0, 0, 0);
  }
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getUKFormattedDate = (dateString) => {
  const { year, month, day } = getDestructedDate(getDateFromString(dateString));
  return `${day}/${month}/${year}`;
};

const getDayOrdinal = (day) => {
  const v = day % 100;
  return DAY_ORDINALS[(v - 20) % 10] || DAY_ORDINALS[v] || DAY_ORDINALS[0];
};

const getTimeSlotFormattedDate = (selectedDateObj) => {
  // Tuesday 24th August 2019
  const dayName = getDayFullName(selectedDateObj.getDay());
  const day = selectedDateObj.getDate();
  const monthName = getMonthFullName(selectedDateObj.getMonth());
  const year = selectedDateObj.getFullYear();
  const ordinal = getDayOrdinal(day);

  return replaceWithNonbreakableSpaces(`${dayName} ${day}${ordinal} ${monthName} ${year}`);
};

const getMonthKey = (selectedDateObj) => {
  const year = selectedDateObj.getFullYear();
  const month = getMonthFullName(selectedDateObj.getMonth());

  return `${year}-${month}`;
};

const getIcsDateTime = (date) => {
  const { year, month, day } = getDestructedDate(date);

  const hours = addZero(date.getHours());
  const minutes = addZero(date.getMinutes());
  const seconds = addZero(date.getSeconds());

  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
};

const getDateTimeObj = (date, ms) => {
  const dateObj = getDateFromString(date);
  return new Date(dateObj.getTime() + parseInt(ms, 10));
};

const getDateWithFirstDayOfMonthAndMonthOffset = (date, offset = 0) => {
  const newDate = getDateFromString(date);
  newDate.setDate(1);
  newDate.setMonth(newDate.getMonth() + offset);
  return getFormattedDate(newDate);
};

const isDateInCalendarRange = (date, maxAdvance) => {
  const currentDateInMs = Date.now();
  const minCalendarDate = new Date(currentDateInMs);
  minCalendarDate.setDate(1);
  minCalendarDate.setHours(0, 0, 0, 0);
  const maxCalendarDate = new Date(currentDateInMs + maxAdvance);
  maxCalendarDate.setDate(1);
  maxCalendarDate.setHours(0, 0, 0, 0);
  const currentDate = getDateFromString(date);
  currentDate.setHours(0, 0, 0, 0);
  return minCalendarDate <= currentDate && currentDate <= maxCalendarDate;
};

const getTimeSlotFormattedTime = (from, to) =>
  replaceWithNonbreakableSpaces(`${msToAmPmHours(from)}–${msToAmPmHours(to)}`);

export const getTimeSlotDescription = (selectedDateSlot, selectedTimeSlot, delimiter = 'at') => {
  const time = getTimeSlotFormattedTime(selectedTimeSlot.from, selectedTimeSlot.to);
  const date = getTimeSlotFormattedDate(getDateFromString(selectedDateSlot));
  return `${date} ${delimiter} ${time}`;
};

const getMonthsRangesForDuration = (stringDate, monthCount = 1) => {
  let resultObject = {};
  const date = getDateFromString(stringDate);
  for (let index = 1; index <= monthCount; index += 1) {
    const { month, year, day, dayNumber = parseInt(day, 10) } = getDestructedDate(date, index);
    const daysInMonth = getDaysInMonth(parseInt(year, 10), parseInt(month, 10) - 1);
    const formatDate = getFormattedDateFromDestructedDate.bind(null, year, month);
    const createDateRange = (startDay, endDay) => ({
      startDate: formatDate(addZero(startDay)),
      endDate: formatDate(addZero(endDay)),
    });

    resultObject = {
      ...resultObject,
      [index]: createDateRange(dayNumber, daysInMonth),
    };
  }
  return resultObject;
};

const getSlotsStartDate = (selectedDate) => {
  const now = new Date();

  if (!selectedDate) {
    return getFormattedDate(now);
  }

  const normalizedSelectedDate = getDateFromString(selectedDate);
  const isDateInFutureYears = normalizedSelectedDate.getFullYear() > now.getFullYear();
  const isDateInFutureMonths = normalizedSelectedDate.getMonth() > now.getMonth();

  if (isDateInFutureYears || isDateInFutureMonths) {
    return getDateWithFirstDayOfMonthAndMonthOffset(getFormattedDate(normalizedSelectedDate));
  }

  return getFormattedDate(now);
};

export const willBookingBeWithinPeriod = (booking, hours = 48) => {
  const now = Date.now();
  const timeShift = 1000 * 60 * 60 * hours;

  const bookingDateTime = +getDateTimeObj(booking.slot.date, booking.slot.time.from);

  return bookingDateTime >= now && bookingDateTime - now <= timeShift;
};

export const dateUtils = {
  getDayFullName,
  getDayOrdinal,
  getMonthFullName,
  getTimeSlotFormattedDate,
  getMonthKey,
  getFormattedDate,
  getDaysInMonth,
  getDateTimeObj,
  getIcsDateTime,
  getUKFormattedDate,
  getDateWithFirstDayOfMonthAndMonthOffset,
  isDateInCalendarRange,
  getDateFromString,
  getMonthsRangesForDuration,
  getSlotsStartDate,
  getTimeSlotFormattedTime,
  DAYS_NAMES,
  DAYS_FULL_NAMES,
  MONTHS_NAMES,
  DAY_ORDINALS,
};
